import type { CSSProperties } from 'react';
import { Toaster as Sonner } from 'sonner';

/** Défaut court : les confirmations ne s'accumulent pas pendant que l'avocat poursuit son travail. */
const DEFAULT_TOAST_DURATION_MS = 3000;

type ToastTimerStyle = CSSProperties & Record<`--toast-${string}`, string>;

/**
 * Options de durée d'un toast, à étaler dans l'appel (`toast.error(msg, { ...toastDuration(8000) })`).
 *
 * La barre de temps en bas du toast (`index.css`, `toast-timer`) est une animation CSS :
 * sonner n'expose pas son minuteur, la barre lit donc sa durée dans `--toast-duration`.
 * Passer `duration` seul ferait diverger la barre et la fermeture réelle, d'où cette
 * fonction unique (et la règle ESLint qui interdit `duration` nu). Un toast sans fin
 * (`Infinity`) n'a pas de barre.
 *
 * Écart accepté : la barre se met en pause au survol (`data-expanded`), mais sonner suspend
 * aussi son minuteur pendant un appui pointeur et quand l'onglet est caché, sans attribut
 * lisible en CSS ; la barre continue alors et le toast reste un peu plus longtemps qu'elle.
 */
export function toastDuration(ms: number): { duration: number; style: ToastTimerStyle } {
  if (ms === Infinity) {
    return { duration: Infinity, style: { '--toast-timer-display': 'none' } };
  }
  return { duration: ms, style: { '--toast-duration': `${ms}ms` } };
}

/**
 * Toaster global, point de rendu unique de toutes les notifications transitoires,
 * en haut à droite avec une barre de temps (modèle PostHog).
 *
 * sonner fusionne `toastOptions.style` puis le `style` de chaque toast sur son `<li>` :
 * le défaut ci-dessous alimente la barre des toasts sans durée explicite.
 */
const Toaster = () => (
  <Sonner
    position="top-right"
    closeButton
    toastOptions={{
      ...toastDuration(DEFAULT_TOAST_DURATION_MS),
      classNames: {
        toast:
          'bg-background border border-border text-foreground shadow-sm !rounded-xl !px-4 !py-3 !pr-12 text-sm',
        description: 'text-muted-foreground text-xs mt-0.5',
        actionButton:
          'bg-charcoal text-white text-xs px-3 py-1 rounded-lg',
        cancelButton:
          'bg-muted text-muted-foreground text-xs px-3 py-1 rounded-lg',
        closeButton:
          '!left-auto !right-2 !top-2 !h-8 !w-8 !translate-x-0 !translate-y-0 !border-border !bg-background !text-muted-foreground hover:!bg-muted hover:!text-foreground',
      },
    }}
  />
);

export { Toaster };
